<script lang="ts">
	import type { LanguageCode, ResolutionString } from '$lib/charting_library/charting_library';
	import { getNumBackInUnits } from '$lib/util/util';
	import BigNumber from 'bignumber.js';
	import { onMount } from 'svelte';
	import {
		chartSelected,
		currentMarket,
		currentMarketAsset,
		currentPosition,
		ordersToShow
	} from '../store';
	import { marketsConfig } from '$lib/config/markets';
	import { getTakeProfitAndStopLossFromOrder, isEntirePositionTPSLOrder } from '../util';
	import { LARGE_POSITIVE_INT } from '$lib/web3/contants';
	import { TRADING_VIEW_MAX_NUM_OF_RERENDERS, TRADING_VIEW_RERENDER_INTERVAL } from './constants';
	import datafeed from './datafeed';

	export let showBorder = true;
	const DEFAULT_OVERRIDES = {
		'paneProperties.background': '#090b12',
		'chartProperties.background': '#090b12',
		'paneProperties.backgroundType': 'solid'
	};
	let innerWidth = 0;
	let innerHeight = 0;
	let w: any;
	let entryLine: any;
	let liquidationLine: any;
	let orderLines: any[] = [];
	let chartReady = false;
	let mountedOnce = false;
	let showOrderLines = true;
	let toggleOLButton: any;
	let toggleOLButtonIcon: any;
	let reRenderCount = 0;

	$: decimalsForAsset =
		$currentMarketAsset &&
		marketsConfig[$currentMarketAsset] &&
		marketsConfig[$currentMarketAsset].showDecimals
			? marketsConfig[$currentMarketAsset].showDecimals
			: 4;

	const handleOrderLinesToggle = () => {
		showOrderLines = !showOrderLines;
		renderOrderLines();
		if (showOrderLines) {
			toggleOLButton.style.setProperty('color', '#1AB19F');
			toggleOLButtonIcon.style.setProperty('border-color', '#1AB19F');
		} else {
			toggleOLButton.style.setProperty('color', '#8387B0');
			toggleOLButtonIcon.style.setProperty('border-color', '#8387B0');
		}
	};

	export const saveChartState = (state: object) => {
		window.localStorage.setItem('trading-view-chart', JSON.stringify(state));
	};

	export const loadChartState = () => {
		const rawChartData = window.localStorage.getItem('trading-view-chart');
		return rawChartData ? JSON.parse(rawChartData) : undefined;
	};

	const handleAutoSave = () => {
		w.save(saveChartState);
	};

	const getChartSymbol = () => {
		if ($currentMarketAsset === 'ETHBTC') {
			return 'ETH/BTC';
		} else if ($currentMarketAsset === 'STETHETH') {
			return 'STETH/ETH';
		} else {
			return `${$currentMarketAsset}/USD`;
		}
	};

	$: chartSymbol = getChartSymbol();

	const renderChart = () => {
		const chartData = loadChartState();

		chartSymbol = chartSymbol.replace('/', '');
		try {
			chartReady = false;
			let widgetConfig = {
				fullscreen: false,
				autosize: true,
				symbol: chartSymbol,
				interval: '15',
				container: 'tradingview_0501f',
				updateFrequency: 100,
				datafeed: datafeed,
				library_path: 'charting_library/',
				locale: 'en' as LanguageCode,
				disabled_features: [
					'header_compare',
					'study_templates',
					'header_symbol_search',
					'display_market_status',
					'create_volume_indicator_by_default',
					'timeframes_toolbar'
				],
				enabled_features: ['hide_left_toolbar_by_default'],
				theme: 'dark',
				overrides: DEFAULT_OVERRIDES,
				toolbar_bg: '#090b12',
				custom_css_url: './theme.css',
				time_frames: [
					{ text: '3M', resolution: '3', description: '3 mins' },
					{ text: '15M', resolution: '15', description: '15 mins' },
					{ text: '1H', resolution: '1H', description: '1 hour' },
					{ text: '2H', resolution: '2H', description: '2 hours' },
					{ text: '4H', resolution: '4H', description: '4 hours' },
					{ text: '6H', resolution: '6H', description: '6 hours' },
					{ text: '8H', resolution: '8H', description: '8 hours' },
					{ text: '12H', resolution: '12H', description: '12 hours' }
				],
				loading_screen: {
					backgroundColor: '#090b12'
				},
				saved_data: chartData
			};
			w = new TradingView.widget(widgetConfig);
			w.onChartReady(() => {
				w.applyOverrides(DEFAULT_OVERRIDES);
				chartReady = true;
				renderLiquidationAndEntryPrice();
				renderOrderLines();
			});
			w.headerReady().then(() => {
				renderToggleOLButton();
			});
			w.subscribe('onAutoSaveNeeded', handleAutoSave);
		} catch (error) {
			if (reRenderCount < TRADING_VIEW_MAX_NUM_OF_RERENDERS) {
				setTimeout(() => {
					renderChart();
				}, TRADING_VIEW_RERENDER_INTERVAL);
				reRenderCount++;
			}
		}
	};

	const renderLiquidationAndEntryPrice = () => {
		if (chartReady) {
			try {
				if (entryLine) entryLine.remove();
				if (liquidationLine) liquidationLine.remove();
			} catch (e) {
				// console.log('Error removing existing lines');
			}
			try {
				if ($currentPosition && $currentMarketAsset) {
					entryLine = w
						.chart()
						.createPositionLine()
						.setText('Avg. entry')
						.setTooltip('Average entry price')
						.setQuantity(
							getNumBackInUnits(new BigNumber($currentPosition.size).abs().toString(), 18, 2) +
								' ' +
								$currentMarketAsset
						)
						.setPrice(getNumBackInUnits($currentPosition.avgEntryPrice, 18, decimalsForAsset))
						.setExtendLeft(false)
						.setBodyBackgroundColor('#11131a')
						.setBodyTextColor('#9096a2')
						.setBodyBorderColor('#212335')
						.setQuantityBorderColor('#212335')
						.setLineLength(20);
					liquidationLine = w
						.chart()
						.createPositionLine()
						.setText('Liq. Price')
						.setTooltip('liquidation price')
						.setQuantity(
							getNumBackInUnits(new BigNumber($currentPosition.size).abs().toString(), 18, 2) +
								' ' +
								$currentMarketAsset
						)
						.setPrice(getNumBackInUnits($currentPosition.liquidationPrice, 18, decimalsForAsset))
						.setExtendLeft(false)
						.setBodyBackgroundColor('#11131a')
						.setBodyTextColor('#9096a2')
						.setBodyBorderColor('#212335')
						.setQuantityBorderColor('#212335')
						.setLineLength(20);
				}
			} catch (e) {
				// console.log('Error while adding chart line: ', e);
			}
		}
	};

	const renderOrderLines = () => {
		if (chartReady) {
			orderLines.forEach((line) => {
				try {
					if (line) line.remove();
				} catch (e) {
					// console.log('Error removing existing Order lines');
				}
			});
			try {
				if ($currentMarket && $ordersToShow && showOrderLines) {
					$ordersToShow.forEach((order) => {
						if (
							order.marketIdentifier === $currentMarket?.marketIdentifier &&
							order.orderType === 'Advanced'
						) {
							if (order.status === 'FirstExecuted') {
								const isPairOrder = order.priceA == null && order.priceB == null;
								const isLong = isPairOrder
									? new BigNumber(order.size).lt(0)
									: new BigNumber(order.size).gt(0);
								let { takeProfit, stopLoss } = getTakeProfitAndStopLossFromOrder(order, isLong);

								const orderSize = order.totalFee
									? new BigNumber(order.size).multipliedBy(-1).toString()
									: order.size;

								const size = isEntirePositionTPSLOrder(order)
									? 'Entire Position'
									: getNumBackInUnits(orderSize, 18, 2) + ' ' + $currentMarketAsset;

								if (new BigNumber(takeProfit).gt(0)) {
									orderLines.push(
										w
											.chart()
											.createPositionLine()
											.setText('TP')
											.setTooltip('Take Profit Size: ' + size)
											.setQuantity(size)
											.setPrice(getNumBackInUnits(takeProfit, 18, decimalsForAsset))
											.setExtendLeft(false)
											.setLineColor('#1AB19F')
											.setQuantityBackgroundColor('#1AB19F')
											.setBodyBackgroundColor('#11131a')
											.setBodyTextColor('#9096a2')
											.setBodyBorderColor('#212335')
											.setQuantityBorderColor('#212335')
											.setLineLength(10)
									);
								}

								if (new BigNumber(stopLoss).gt(0)) {
									orderLines.push(
										w
											.chart()
											.createPositionLine()
											.setText('SL')
											.setTooltip('Stop Loss Size: ' + size)
											.setQuantity(size)
											.setPrice(getNumBackInUnits(stopLoss, 18, decimalsForAsset))
											.setExtendLeft(false)
											.setLineColor('#e3517b')
											.setQuantityBackgroundColor('#e3517b')
											.setBodyBackgroundColor('#11131a')
											.setBodyTextColor('#9096a2')
											.setBodyBorderColor('#212335')
											.setQuantityBorderColor('#212335')
											.setLineLength(10)
									);
								}
							} else if (order.status === 'Submitted') {
								const isLong = new BigNumber(order.size).gt(0);

								let orderText = 'Limit';
								if (order.size && isLong) {
									orderText = order.priceA && new BigNumber(order.priceA).eq(1) ? 'Limit' : 'Stop';
								} else {
									orderText = order.priceA && new BigNumber(order.priceA).eq(1) ? 'Stop' : 'Limit';
								}

								let price;
								if (order.priceB >= LARGE_POSITIVE_INT) {
									price = order.priceA;
								} else price = order.priceB;

								const color = isLong ? '#58BD7D' : '#E65757';
								orderLines.push(
									w
										.chart()
										.createPositionLine()
										.setText(orderText)
										.setTooltip(
											orderText +
												' Size: ' +
												getNumBackInUnits(order.size, 18, 2) +
												' ' +
												$currentMarketAsset
										)
										.setQuantity(getNumBackInUnits(order.size, 18, 2) + ' ' + $currentMarketAsset)
										.setPrice(getNumBackInUnits(price, 18, decimalsForAsset))
										.setExtendLeft(false)
										.setQuantityBackgroundColor(color)
										.setLineColor(color)
										.setBodyBackgroundColor('#11131a')
										.setBodyTextColor('#9096a2')
										.setBodyBorderColor('#212335')
										.setQuantityBorderColor('#212335')
										.setLineLength(10)
								);
							}
						}
					});
				}
			} catch (e) {
				// console.log('Error while adding chart line: ', e);
			}
		}
	};

	const renderToggleOLButton = () => {
		try {
			toggleOLButton = w.createButton();
			toggleOLButton.setAttribute('title', 'Show/Hide Order Lines');
			toggleOLButton.addEventListener('click', handleOrderLinesToggle);
			toggleOLButton.style.setProperty('font-size', '14px');
			toggleOLButton.style.setProperty('color', '#1AB19F');
			toggleOLButton.style.setProperty('display', 'flex');
			toggleOLButton.style.setProperty('align-items', 'center');
			toggleOLButton.style.setProperty('cursor', 'pointer');

			toggleOLButtonIcon = document.createElement('span');
			toggleOLButtonIcon.innerHTML = '╾';
			toggleOLButtonIcon.style.setProperty('margin-right', '6px');
			toggleOLButtonIcon.style.setProperty('margin-top', '-4px');
			toggleOLButtonIcon.style.setProperty('font-size', '32px');
			toggleOLButtonIcon.style.setProperty('border-radius', '4px');
			toggleOLButtonIcon.style.setProperty('padding', '4px 4px');
			toggleOLButton.appendChild(toggleOLButtonIcon);

			const text = document.createElement('span');
			text.innerHTML = 'Order Lines';
			toggleOLButton.appendChild(text);
		} catch (error) {
			console.log({ error });
		}
	};

	$: if ($currentPosition || chartReady || $currentMarketAsset) {
		renderLiquidationAndEntryPrice();
	}

	$: if ($ordersToShow || chartReady || $currentMarketAsset) {
		renderOrderLines();
	}
	onMount(() => {
		if ($currentMarketAsset && !mountedOnce) {
			renderChart();
		}
		mountedOnce = true;
	});

	$: if ($currentMarketAsset && mountedOnce && chartReady) {
		w.setSymbol(`Crypto.${getChartSymbol()}`, '15' as ResolutionString, () => {
			chartReady = true;
			renderLiquidationAndEntryPrice();
		});
	}
</script>

<svelte:window bind:innerWidth bind:innerHeight />
<div
	class="tradingview-widget-container w-full h-full p-1 sm:min-h-[45vh] sm:h-[45vh] border-fx-grey-500"
	class:border-r={showBorder}
	class:hidden={$chartSelected != 'TradingView'}
>
	<div id="tradingview_0501f" class="w-full h-full" />
</div>
